/**
 * IdeasInput.js
 * Topic input + platform selector + example chips + submit button.
 * Pure presentation — all state owned by GenerateIdeas orchestrator.
 */
import React from 'react';
import {
    Box, Paper, Typography, TextField, Button, Chip,
    Avatar, CircularProgress, alpha,
} from '@mui/material';
import {
    AutoAwesome as AutoAwesomeIcon,
    Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { PLATFORMS, EXAMPLE_TOPICS, PLATFORM_COLORS } from './constants';

export default function IdeasInput({
    topic, onTopicChange,
    platform, onPlatformChange,
    onSubmit, loading, isValid,
    card,
}) {
    return (
        <Paper elevation={0} sx={{ ...card, mb: 0 }}>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Avatar sx={{ bgcolor: alpha('#6366f1', 0.12), color: '#6366f1', width: 38, height: 38 }}>
                    <PsychologyIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Topic & Platform
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Enter a niche or theme to generate tailored ideas
                    </Typography>
                </Box>
            </Box>

            {/* Topic field */}
            <TextField
                fullWidth
                placeholder="e.g. healthy recipes, tech gadgets, morning routines…"
                value={topic}
                onChange={onTopicChange}
                disabled={loading}
                variant="outlined"
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5, fontSize: '0.92rem',
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6366f1',
                            boxShadow: `0 0 0 3px ${alpha('#6366f1', 0.1)}`,
                        },
                    },
                }}
            />

            {/* Example topic chips */}
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}>
                Popular topics:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                {EXAMPLE_TOPICS.map(t => (
                    <Chip
                        key={t} label={t} size="small"
                        onClick={() => onTopicChange({ target: { value: t } })}
                        sx={{
                            bgcolor: topic === t ? alpha('#6366f1', 0.15) : alpha('#6366f1', 0.07),
                            color: '#6366f1',
                            border: `1px solid ${alpha('#6366f1', topic === t ? 0.4 : 0.15)}`,
                            fontWeight: topic === t ? 700 : 500,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha('#6366f1', 0.15), transform: 'translateY(-1px)' },
                            transition: 'all 0.15s ease',
                        }}
                    />
                ))}
            </Box>

            {/* Platform filter */}
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}>
                Filter by platform:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
                {PLATFORMS.map(p => {
                    const isSelected = platform === p.value;
                    const color = p.value ? (PLATFORM_COLORS[p.value] || '#6366f1') : '#6366f1';
                    return (
                        <Chip
                            key={String(p.value)}
                            label={`${p.icon} ${p.label}`}
                            size="small"
                            onClick={() => onPlatformChange(p.value)}
                            sx={{
                                bgcolor: isSelected ? alpha(color, 0.15) : 'transparent',
                                color: isSelected ? color : 'text.secondary',
                                border: `1px solid ${isSelected ? alpha(color, 0.4) : alpha('#9ca3af', 0.3)}`,
                                fontWeight: isSelected ? 700 : 500,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(color, 0.1), borderColor: alpha(color, 0.3) },
                                transition: 'all 0.15s ease',
                            }}
                        />
                    );
                })}
            </Box>

            {/* Submit */}
            <Button
                fullWidth variant="contained"
                onClick={onSubmit}
                disabled={!isValid}
                size="large"
                startIcon={
                    loading
                        ? <CircularProgress size={18} sx={{ color: 'white' }} />
                        : <AutoAwesomeIcon />
                }
                sx={{
                    borderRadius: 2.5, py: 1.4, fontWeight: 700, fontSize: '0.95rem',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #5855eb, #7c3aed)',
                        boxShadow: '0 6px 24px rgba(99,102,241,0.45)',
                        transform: 'translateY(-1px)',
                    },
                    '&:disabled': { background: 'rgba(156,163,175,0.35)', color: 'rgba(255,255,255,0.65)' },
                    transition: 'all 0.2s ease',
                }}
            >
                {loading ? 'Generating…' : 'Generate Ideas'}
            </Button>
        </Paper>
    );
}
